import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { PageHeader, Tabs } from '../components/Layout';
import { Empty, ErrorState, Field, Loading, Modal, Select, TextInput, useToast } from '../components/ui';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date } from '../lib/format';
import { ClosuresTab } from './privacy/ClosuresTab';
import { DisclosuresTab } from './privacy/DisclosuresTab';
import { IncidentsTab } from './privacy/IncidentsTab';
import { PlatformProcessorsTab } from './privacy/ProcessorsTab';
import { RetentionTab } from './privacy/RetentionTab';
import { PlatformSupportAccessTab } from './privacy/SupportAccessTabs';

/**
 * SARTHI's own privacy governance, from the platform console.
 *
 * Notices: the privacy policy, DPA and other documents, drafted from
 * templates whose placeholders must be replaced before publishing.
 * Purposes: where the legal decisions on each purpose's basis are recorded.
 * Retention: how long each kind of record is kept, and what happens after.
 *
 * Support access: the windows staff opened onto hotel accounts.
 * Disclosures: requests from authorities, and the decision on each.
 *
 * Incidents: the security incident register, fed by alerts and people.
 *
 * Processors: the register of third parties personal data reaches.
 */

type Tab = 'notices' | 'purposes' | 'retention' | 'support' | 'disclosures' | 'incidents' | 'closures' | 'processors';

interface Notice {
  id: string;
  type: string;
  typeLabel: string;
  version: string;
  title: string;
  summary: string;
  content: string;
  status: 'DRAFT' | 'PUBLISHED' | 'RETIRED';
  requiresAcceptance: boolean;
  hasPlaceholders: boolean;
  publishedAt: string | null;
}

interface Purpose {
  purpose: string;
  label: string;
  description: string;
  processingBasis: 'UNDECIDED' | 'CONSENT' | 'LEGITIMATE_USE' | 'LEGAL_OBLIGATION';
  consentRequired: boolean | null;
  notes: string | null;
}

const NOTICE_TYPES: Array<[string, string]> = [
  ['PRIVACY_POLICY', 'Privacy Policy'],
  ['GUEST_PRIVACY_NOTICE', 'Guest privacy notice (default for hotels)'],
  ['COOKIE_NOTICE', 'Cookie and storage notice'],
  ['DPA', 'Data Processing Agreement'],
  ['REFUND_POLICY', 'Refund policy'],
  ['GRIEVANCE_PROCEDURE', 'Grievance procedure'],
  ['RETENTION_POLICY', 'Data retention policy'],
  ['INCIDENT_RESPONSE_POLICY', 'Incident response policy'],
  ['GOVERNMENT_DISCLOSURE_PROCEDURE', 'Government disclosure procedure'],
  ['SUPPORT_ACCESS_POLICY', 'Support access policy'],
  ['SUBPROCESSOR_REGISTER', 'Subprocessor register'],
];

export function PlatformPrivacyPage() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const [tab, setTab] = useState<Tab>('notices');
  return (
    <div className="stack">
      <PageHeader
        title="Privacy & Governance"
        subtitle="SARTHI's notices and policies, and the decisions behind each purpose"
      />
      <Tabs
        tabs={[
          { id: 'notices', label: 'Notices & policies' },
          { id: 'purposes', label: 'Purposes' },
          { id: 'retention', label: 'Retention' },
          { id: 'incidents', label: 'Incidents' },
          { id: 'support', label: 'Support access' },
          { id: 'closures', label: 'Account closures' },
          { id: 'processors', label: 'Processors' },
          ...(isSuperadmin ? [{ id: 'disclosures' as const, label: 'Disclosures' }] : []),
        ]}
        active={tab}
        onChange={setTab}
      />
      {tab === 'notices' ? <NoticesTab /> : null}
      {tab === 'purposes' ? <PurposesTab /> : null}
      {tab === 'retention' ? <RetentionTab /> : null}
      {tab === 'incidents' ? <IncidentsTab /> : null}
      {tab === 'support' ? <PlatformSupportAccessTab /> : null}
      {tab === 'closures' ? <ClosuresTab /> : null}
      {tab === 'processors' ? <PlatformProcessorsTab /> : null}
      {tab === 'disclosures' && isSuperadmin ? <DisclosuresTab /> : null}
    </div>
  );
}

function NoticesTab() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Notice | 'new' | null>(null);
  const [reading, setReading] = useState<Notice | null>(null);

  const notices = useQuery({
    queryKey: ['platform-notices'],
    queryFn: () => api.get<Notice[]>('/api/platform/privacy/notices'),
  });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['platform-notices'] });

  const templates = useMutation({
    mutationFn: () => api.post<{ created: string[] }>('/api/platform/privacy/notices/templates'),
    onSuccess: (result) => {
      push(
        result.created.length > 0
          ? `Drafted ${result.created.length} document(s) from templates`
          : 'Every document already has a draft or version',
        'ok',
      );
      refresh();
    },
    onError: fail,
  });

  const act = useMutation({
    mutationFn: ({ id, action }: { id: string; action: 'publish' | 'retire' }) =>
      api.post(`/api/platform/privacy/notices/${id}/${action}`),
    onSuccess: () => {
      push('Done', 'ok');
      refresh();
    },
    onError: fail,
  });

  if (notices.isLoading) return <Loading />;
  if (notices.error) return <ErrorState error={notices.error} onRetry={() => void notices.refetch()} />;

  return (
    <div className="card">
      <div className="card-head row between">
        <h3>Notices and policies</h3>
        {isSuperadmin ? (
          <div className="row gap-6">
            <button className="btn sm" disabled={templates.isPending} onClick={() => templates.mutate()}>
              Draft missing documents
            </button>
            <button className="btn primary sm" onClick={() => setEditing('new')}>
              New version
            </button>
          </div>
        ) : null}
      </div>
      <div className="card-body small muted">
        Templates describe what SARTHI actually does and mark everything a person must supply or a
        lawyer must approve. A document cannot be published until every marker is replaced.
        Published versions are never edited; draft a new version instead.
      </div>
      {(notices.data ?? []).length === 0 ? (
        <Empty message="No documents yet. Draft them from the templates." />
      ) : (
        <div className="table-wrap">
          <table className="tbl tbl-stack">
            <thead>
              <tr>
                <th>Document</th>
                <th>Version</th>
                <th>Status</th>
                <th>Published</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {notices.data!.map((notice) => (
                <tr key={notice.id}>
                  <td>
                    {notice.typeLabel}
                    {notice.requiresAcceptance ? <span className="chip info" style={{ marginLeft: 6 }}>acceptance</span> : null}
                    {notice.hasPlaceholders ? <span className="chip warn" style={{ marginLeft: 6 }}>needs input</span> : null}
                  </td>
                  <td className="mono">{notice.version}</td>
                  <td>
                    <span className={`chip ${notice.status === 'PUBLISHED' ? 'ok' : notice.status === 'DRAFT' ? 'warn' : ''}`}>
                      {notice.status}
                    </span>
                  </td>
                  <td>{notice.publishedAt ? date(notice.publishedAt) : '—'}</td>
                  <td>
                    <div className="row gap-6">
                      <button className="btn sm" onClick={() => setReading(notice)}>
                        Read
                      </button>
                      {isSuperadmin && notice.status === 'DRAFT' ? (
                        <>
                          <button className="btn sm" onClick={() => setEditing(notice)}>
                            Edit
                          </button>
                          <button
                            className="btn sm primary"
                            disabled={act.isPending || notice.hasPlaceholders}
                            title={notice.hasPlaceholders ? 'Replace the marked text first' : undefined}
                            onClick={() => act.mutate({ id: notice.id, action: 'publish' })}
                          >
                            Publish
                          </button>
                        </>
                      ) : null}
                      {isSuperadmin && notice.status === 'PUBLISHED' ? (
                        <button className="btn sm" disabled={act.isPending} onClick={() => act.mutate({ id: notice.id, action: 'retire' })}>
                          Retire
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editing ? (
        <PlatformNoticeEditor
          notice={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={refresh}
        />
      ) : null}
      {reading ? (
        <Modal title={`${reading.title} · v${reading.version}`} onClose={() => setReading(null)}>
          <p className="small b">{reading.summary}</p>
          <div className="small privacy-doc">{reading.content}</div>
        </Modal>
      ) : null}
    </div>
  );
}

function PlatformNoticeEditor({
  notice,
  onClose,
  onSaved,
}: {
  notice: Notice | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { push, fail } = useToast();
  const [type, setType] = useState(notice?.type ?? 'PRIVACY_POLICY');
  const [version, setVersion] = useState('');
  const [title, setTitle] = useState(notice?.title ?? '');
  const [summary, setSummary] = useState(notice?.summary ?? '');
  const [content, setContent] = useState(notice?.content ?? '');
  const [requiresAcceptance, setRequiresAcceptance] = useState(notice?.requiresAcceptance ?? false);

  const save = useMutation({
    mutationFn: () =>
      notice
        ? api.patch(`/api/platform/privacy/notices/${notice.id}`, { title, summary, content, requiresAcceptance })
        : api.post('/api/platform/privacy/notices', { type, version, title, summary, content, requiresAcceptance }),
    onSuccess: () => {
      push('Draft saved', 'ok');
      onSaved();
      onClose();
    },
    onError: fail,
  });

  return (
    <Modal
      title={notice ? `Edit ${notice.typeLabel} ${notice.version}` : 'New document version'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={save.isPending || !title.trim() || !summary.trim() || !content.trim() || (!notice && !version.trim())}
            onClick={() => save.mutate()}
          >
            Save draft
          </button>
        </>
      }
    >
      <div className="col gap-14">
        {!notice ? (
          <>
            <Select label="Document" value={type} onChange={(e) => setType(e.target.value)}>
              {NOTICE_TYPES.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
            <TextInput label="Version" placeholder="e.g. 2026-10-06" value={version} onChange={(e) => setVersion(e.target.value)} />
          </>
        ) : null}
        <TextInput label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Field label="Summary">
          <textarea className="input" rows={3} value={summary} onChange={(e) => setSummary(e.target.value)} />
        </Field>
        <Field label="Full text">
          <textarea className="input" rows={16} value={content} onChange={(e) => setContent(e.target.value)} />
        </Field>
        <label className="row gap-6 small">
          <input type="checkbox" checked={requiresAcceptance} onChange={(e) => setRequiresAcceptance(e.target.checked)} />
          Hotel owners must accept this version (they are asked once it is published)
        </label>
      </div>
    </Modal>
  );
}

function PurposesTab() {
  const { session } = useAuth();
  const isSuperadmin = session?.platformRole === 'SUPERADMIN';
  const purposes = useQuery({
    queryKey: ['platform-purposes'],
    queryFn: () => api.get<Purpose[]>('/api/platform/privacy/purposes'),
  });
  if (purposes.isLoading) return <Loading />;
  if (purposes.error) return <ErrorState error={purposes.error} onRetry={() => void purposes.refetch()} />;
  return (
    <div className="card">
      <div className="card-head">
        <h3>Purposes</h3>
      </div>
      <div className="card-body small muted">
        Record each decision here once legal review has made it. While a purpose is undecided, no
        consent question is asked for it anywhere. Marking consent as required makes the question
        appear on every guest form, unticked.
      </div>
      <div className="table-wrap">
        <table className="tbl tbl-stack">
          <thead>
            <tr>
              <th>Purpose</th>
              <th>Basis</th>
              <th>Consent</th>
              <th>Notes</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {purposes.data!.map((row) => (
              <PurposeRow key={row.purpose} row={row} editable={isSuperadmin} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PurposeRow({ row, editable }: { row: Purpose; editable: boolean }) {
  const { push, fail } = useToast();
  const queryClient = useQueryClient();
  const [basis, setBasis] = useState(row.processingBasis);
  const [consent, setConsent] = useState(row.consentRequired === null ? '' : String(row.consentRequired));
  const [notes, setNotes] = useState(row.notes ?? '');
  const dirty =
    basis !== row.processingBasis ||
    consent !== (row.consentRequired === null ? '' : String(row.consentRequired)) ||
    notes !== (row.notes ?? '');

  const save = useMutation({
    mutationFn: () =>
      api.put(`/api/platform/privacy/purposes/${row.purpose}`, {
        processingBasis: basis,
        consentRequired: consent === '' ? null : consent === 'true',
        notes,
      }),
    onSuccess: () => {
      push(`${row.label} saved`, 'ok');
      void queryClient.invalidateQueries({ queryKey: ['platform-purposes'] });
    },
    onError: fail,
  });

  return (
    <tr>
      <td>
        <b>{row.label}</b>
        <div className="xs muted">{row.description}</div>
      </td>
      <td>
        <select className="select" disabled={!editable} value={basis} onChange={(e) => setBasis(e.target.value as Purpose['processingBasis'])}>
          <option value="UNDECIDED">Undecided</option>
          <option value="CONSENT">Consent</option>
          <option value="LEGITIMATE_USE">Legitimate use</option>
          <option value="LEGAL_OBLIGATION">Legal obligation</option>
        </select>
      </td>
      <td>
        <select className="select" disabled={!editable} value={consent} onChange={(e) => setConsent(e.target.value)}>
          <option value="">Undecided</option>
          <option value="true">Required</option>
          <option value="false">Not required</option>
        </select>
      </td>
      <td>
        <input className="input" disabled={!editable} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </td>
      <td>
        {editable ? (
          <button className="btn sm primary" disabled={!dirty || save.isPending} onClick={() => save.mutate()}>
            Save
          </button>
        ) : null}
      </td>
    </tr>
  );
}
